"use client";

import React, { useState, useEffect } from "react";
import { Section } from "@/components/ui/section";
import { m as motion } from "framer-motion";
import { ABOUT_DATA, SECTION_METADATA } from "@/lib/data";


const BuildStatus = React.memo(({ statuses }: { statuses?: string[] }) => {
  const finalStatuses =
    statuses && statuses.length > 0 ? statuses : ABOUT_DATA.buildStatuses;

  const [currentStatus, setCurrentStatus] = useState(finalStatuses[0]);
  const [statusIndex, setStatusIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setStatusIndex((prev) => (prev + 1) % finalStatuses.length);
      setCurrentStatus(finalStatuses[(statusIndex + 1) % finalStatuses.length]);
    }, 4000);

    return () => clearInterval(interval);
  }, [statusIndex, finalStatuses]);

  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-3">
      <motion.div
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0"
      />
      <motion.span
        key={statusIndex}
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -5 }}
        transition={{ duration: 0.4 }}
        className="font-medium"
      >
        {currentStatus}
      </motion.span>
    </div>
  );
});

BuildStatus.displayName = "BuildStatus";

export default function About({ data }: { data?: any }) {
  const aboutData = data || ABOUT_DATA;

  const finalData = {
    intro: aboutData?.intro || ABOUT_DATA.intro,
    body: aboutData?.body || ABOUT_DATA.body,
    currentFocus: aboutData?.currentFocus || ABOUT_DATA.currentFocus,
    location: aboutData?.location || ABOUT_DATA.location,
    buildStatuses: aboutData?.buildStatuses || ABOUT_DATA.buildStatuses,
  };

  return (
    <Section id="about" className="py-16 md:py-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto px-4 sm:px-6 space-y-16 md:space-y-20"
      >
        <div className="space-y-4 md:space-y-6 text-center">
          <h2 className="text-[9px] md:text-xs font-mono uppercase tracking-[0.4em] text-muted-foreground opacity-70">
            {SECTION_METADATA.about.sectionNumber} /{" "}
            {SECTION_METADATA.about.sectionLabel}
          </h2>
          <h3 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
            {SECTION_METADATA.about.title}
          </h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[2.5fr_1fr] gap-8 lg:gap-16">
          <div className="space-y-6 sm:space-y-8 text-muted-foreground/90 leading-relaxed text-left px-2 sm:px-0">
            <p className="text-sm sm:text-lg md:text-xl font-medium text-foreground/80">
              {finalData.intro}
            </p>
            <div className="space-y-5 text-[13px] sm:text-base md:text-lg">
              {finalData.body &&
                Array.isArray(finalData.body) &&
                finalData.body.map((paragraph: string, idx: number) => (
                  <p key={idx} className="leading-snug sm:leading-relaxed">{paragraph}</p>
                ))}
            </div>
          </div>

          <div className="space-y-4 md:space-y-6">
            <div className="p-5 sm:p-7 border border-border/40 rounded-3xl bg-secondary/10 backdrop-blur-md shadow-sm hover:shadow-md transition-shadow duration-300">
              <h3 className="text-[9px] sm:text-xs font-black text-primary uppercase tracking-[0.25em] mb-2 sm:mb-3">
                Current Focus
              </h3>
              <p className="text-[11px] sm:text-sm md:text-base text-foreground font-medium leading-relaxed">
                {finalData.currentFocus}
              </p>
              <BuildStatus statuses={finalData.buildStatuses} />
            </div>

            <div className="p-5 sm:p-7 border border-border/40 rounded-3xl bg-secondary/10 backdrop-blur-md shadow-sm hover:shadow-md transition-shadow duration-300">
              <h3 className="text-[9px] sm:text-xs font-black text-primary uppercase tracking-[0.25em] mb-2 sm:mb-3">
                Location
              </h3>
              <p className="text-[11px] sm:text-sm md:text-base text-foreground font-medium flex items-center gap-2">
                <span className="flex h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                {finalData.location}
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </Section>
  );
}
